import {
  CustomerWaiver,
  CustomerWaiversPaginatedResponse,
  SeasonListItem,
} from '@types';
import { Pagination, PDFViewerModal, SearchBar } from '_components/atoms';
import AdminService from '_services/AdminService';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';
import { MdClose, MdFlag, MdOutlineFlag } from 'react-icons/md';

const formatSeasonLabel = (season: SeasonListItem) => {
  const start = HelperMethods.formatDate(season.startDate);
  const end = HelperMethods.formatDate(season.endDate);
  return season.isActive ? `${start} – ${end} (Active)` : `${start} – ${end}`;
};

const pickDefaultSeasonId = (seasons: SeasonListItem[]): number | '' => {
  const inactive = seasons.find(s => !s.isActive);
  if (inactive) return inactive.id;
  const active = seasons.find(s => s.isActive);
  return active ? active.id : '';
};

const ArchivedWaivers: React.FC = () => {
  const [seasons, setSeasons] = useState<SeasonListItem[]>([]);
  const [seasonId, setSeasonId] = useState<number | ''>('');
  const [seasonsLoading, setSeasonsLoading] = useState(true);
  const [waivers, setWaivers] = useState<CustomerWaiver[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });
  const [flagModal, setFlagModal] = useState<{
    isOpen: boolean;
    waiver: CustomerWaiver | null;
  }>({
    isOpen: false,
    waiver: null,
  });
  const [flagReason, setFlagReason] = useState('');
  const [isFlaggingWaiver, setIsFlaggingWaiver] = useState(false);

  useEffect(() => {
    const loadSeasons = async () => {
      try {
        setSeasonsLoading(true);
        const response = (await AdminService.getSeasons()) as unknown as
          | SeasonListItem[]
          | null;
        const list = Array.isArray(response) ? response : [];
        setSeasons(list);
        setSeasonId(pickDefaultSeasonId(list));
      } catch (error) {
        console.error('Error fetching seasons:', error);
      } finally {
        setSeasonsLoading(false);
      }
    };

    loadSeasons();
  }, []);

  const fetchWaivers = useCallback(async () => {
    if (!seasonId) {
      setWaivers([]);
      setTotalRecords(0);
      setTotalPages(0);
      setHasNext(false);
      setHasPrevious(false);
      return;
    }

    try {
      setLoading(true);
      const responseData = (await AdminService.getArchivedWaivers({
        seasonId: Number(seasonId),
        pageNumber,
        pageSize,
        searchTerm: searchTerm || undefined,
      })) as unknown as CustomerWaiversPaginatedResponse;

      if (responseData) {
        setWaivers(responseData.waivers || []);
        setTotalRecords(responseData.pagination?.totalRecords || 0);
        setTotalPages(responseData.pagination?.totalPages || 0);
        setHasNext(responseData.pagination?.hasNext || false);
        setHasPrevious(responseData.pagination?.hasPrevious || false);
      }
    } catch (error) {
      console.error('Error fetching archived waivers:', error);
    } finally {
      setLoading(false);
    }
  }, [seasonId, pageNumber, pageSize, searchTerm]);

  useEffect(() => {
    fetchWaivers();
  }, [fetchWaivers]);

  const handleSearch = useCallback((term: string) => {
    if (term.length >= 3 || term.length === 0) {
      setSearchTerm(term);
      setPageNumber(1);
    }
  }, []);

  const handleSeasonChange = (value: string) => {
    setSeasonId(value ? Number(value) : '');
    setPageNumber(1);
  };

  const handlePageChange = (newPage: number) => setPageNumber(newPage);
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({ isOpen: true, url, title });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({ isOpen: false, url: '', title: '' });
  };

  const openFlagModal = (waiver: CustomerWaiver) => {
    setFlagModal({ isOpen: true, waiver });
    setFlagReason(waiver.flagReason || '');
  };

  const closeFlagModal = () => {
    setFlagModal({ isOpen: false, waiver: null });
    setFlagReason('');
  };

  const handleFlagWaiver = async () => {
    if (!flagModal.waiver) return;

    try {
      setIsFlaggingWaiver(true);
      const newFlagStatus = !flagModal.waiver.isFlagged;

      await AdminService.updateCustomerWaiverFlag(
        flagModal.waiver.barcodeId,
        flagModal.waiver.id,
        newFlagStatus,
        flagReason,
      );

      setWaivers(prevWaivers =>
        prevWaivers.map(w =>
          w.id === flagModal.waiver!.id
            ? { ...w, isFlagged: newFlagStatus, flagReason }
            : w,
        ),
      );

      closeFlagModal();
    } catch (error) {
      console.error('Error updating waiver flag:', error);
    } finally {
      setIsFlaggingWaiver(false);
    }
  };

  const emptyMessage = !seasonId
    ? 'Select a season'
    : loading
      ? 'Loading...'
      : 'No waivers found for this season';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Archived Waivers</h1>
          <p className="mt-1 text-sm text-gray-600">
            View signed customer waivers for any season, including inactive ones
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Records: <span className="font-semibold">{totalRecords}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="sm:min-w-[280px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Season
          </label>
          <select
            value={seasonId === '' ? '' : String(seasonId)}
            onChange={e => handleSeasonChange(e.target.value)}
            disabled={seasonsLoading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">
              {seasonsLoading ? 'Loading seasons...' : 'Select a season'}
            </option>
            {seasons.map(season => (
              <option key={season.id} value={season.id}>
                {formatSeasonLabel(season)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <SearchBar
        placeholder="Search by skater name, email, barcode ID..."
        onSearch={handleSearch}
        debounceMs={1000}
      />

      <Pagination
        pageNumber={pageNumber}
        totalPages={totalPages}
        pageSize={pageSize}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onReload={fetchWaivers}
      />

      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Skater Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Waiver Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {waivers.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                waivers.map(waiver => (
                  <React.Fragment key={waiver.id + waiver.barcodeId}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-bold text-gray-900">
                            {waiver.skaterName}
                          </div>
                          {waiver.isFlagged && (
                            <MdFlag
                              className="text-red-600"
                              size={20}
                              title={waiver.flagReason || 'Flagged'}
                            />
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          DOB:{' '}
                          {HelperMethods.formatDate(waiver.dateOfBirth) || '—'}
                        </div>
                        <div className="text-xs text-gray-700">
                          Barcode:{' '}
                          <span className="font-semibold text-blue-600">
                            {waiver.barcodeId}
                          </span>
                        </div>
                        {typeof waiver.isAdult === 'boolean' && (
                          <div className="mt-1">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                waiver.isAdult
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}
                            >
                              {waiver.isAdult ? 'Adult' : 'Minor'}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {waiver.email}
                        </div>
                        <div className="text-sm text-gray-500">
                          {waiver.phoneNumber}
                        </div>
                        {waiver.emergencyContactPhone && (
                          <div className="text-xs text-gray-500 mt-1">
                            Emergency: {waiver.emergencyContactPhone}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {HelperMethods.formatWaiverSignedBy(
                          waiver.signedBy,
                        ) && (
                          <div className="text-sm text-gray-900">
                            Signed by:{' '}
                            {HelperMethods.formatWaiverSignedBy(
                              waiver.signedBy,
                            )}
                          </div>
                        )}
                        {waiver.relationship && (
                          <div className="text-sm text-gray-500">
                            Relationship: {waiver.relationship}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          Submitted:{' '}
                          {HelperMethods.formatDateTimeLocal(
                            waiver.submittedAt,
                          )}
                        </div>
                        <div className="mt-1">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              waiver.accepted
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {waiver.accepted ? 'Accepted' : 'Needs Waiver'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-col items-center gap-2">
                          <div className="flex space-x-2 justify-center">
                            {waiver.pdfFilePath ? (
                              <button
                                onClick={() =>
                                  openPdfViewer(
                                    waiver.pdfFilePath!,
                                    `Waiver - ${waiver.skaterName}`,
                                  )
                                }
                                className="text-blue-600 hover:text-blue-900"
                              >
                                View Waiver
                              </button>
                            ) : waiver.waiverType === 'Paper' ? (
                              <div className="text-xs text-green-700 text-center">
                                <div className="font-semibold">
                                  Paper Waiver
                                </div>
                                {waiver.paperWaiverCompletedAt && (
                                  <div className="text-gray-500">
                                    {HelperMethods.formatDateTimeLocal(
                                      waiver.paperWaiverCompletedAt,
                                    )}
                                  </div>
                                )}
                                {waiver.paperWaiverRecordedByName && (
                                  <div className="text-gray-500">
                                    by {waiver.paperWaiverRecordedByName}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-gray-500">Needs Waiver</div>
                            )}
                          </div>
                          <button
                            onClick={() => openFlagModal(waiver)}
                            className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-medium transition-colors ${
                              waiver.isFlagged
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {waiver.isFlagged ? (
                              <>
                                <MdFlag size={14} />
                                Unflag
                              </>
                            ) : (
                              <>
                                <MdOutlineFlag size={14} />
                                Flag
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {waiver.isFlagged && waiver.flagReason && (
                      <tr className="bg-red-50">
                        <td colSpan={4} className="px-6 py-3">
                          <div className="flex items-start gap-2">
                            <MdFlag
                              className="text-red-600 flex-shrink-0 mt-0.5"
                              size={16}
                            />
                            <div className="flex-1">
                              <span className="text-xs font-semibold text-red-800 uppercase">
                                Flag Reason:
                              </span>
                              <p className="text-sm text-red-900 mt-1">
                                {waiver.flagReason}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden space-y-4">
        {waivers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            {emptyMessage}
          </div>
        ) : (
          waivers.map(waiver => (
            <div
              key={waiver.id + waiver.barcodeId}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {waiver.skaterName}
                    </h3>
                    {waiver.isFlagged && (
                      <MdFlag
                        className="text-red-600"
                        size={20}
                        title={waiver.flagReason || 'Flagged'}
                      />
                    )}
                  </div>
                  {typeof waiver.isAdult === 'boolean' && (
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        waiver.isAdult
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {waiver.isAdult ? 'Adult' : 'Minor'}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  DOB: {HelperMethods.formatDate(waiver.dateOfBirth) || '—'}
                </div>
                <div className="text-xs text-gray-700 mt-1">
                  Barcode:{' '}
                  <span className="font-semibold text-blue-600">
                    {waiver.barcodeId}
                  </span>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Contact Information
                </h4>
                <div className="text-sm text-gray-600">
                  <div>{waiver.email}</div>
                  <div>{waiver.phoneNumber}</div>
                  {waiver.emergencyContactPhone && (
                    <div className="mt-1">
                      Emergency: {waiver.emergencyContactPhone}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Waiver Details
                </h4>
                <div className="text-sm text-gray-600">
                  {HelperMethods.formatWaiverSignedBy(waiver.signedBy) && (
                    <div>
                      Signed by:{' '}
                      {HelperMethods.formatWaiverSignedBy(waiver.signedBy)}
                    </div>
                  )}
                  {waiver.relationship && (
                    <div>Relationship: {waiver.relationship}</div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Submitted:{' '}
                    {HelperMethods.formatDateTimeLocal(waiver.submittedAt)}
                  </div>
                  <div className="mt-2">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        waiver.accepted
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {waiver.accepted ? 'Accepted' : 'Needs Waiver'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {waiver.pdfFilePath ? (
                  <button
                    onClick={() =>
                      openPdfViewer(
                        waiver.pdfFilePath!,
                        `Waiver - ${waiver.skaterName}`,
                      )
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    View Waiver
                  </button>
                ) : waiver.waiverType === 'Paper' ? (
                  <div className="text-xs text-green-700 px-3 py-2 bg-green-50 rounded-md">
                    <div className="font-semibold">Paper Waiver</div>
                    {waiver.paperWaiverCompletedAt && (
                      <div className="text-gray-500">
                        {HelperMethods.formatDateTimeLocal(
                          waiver.paperWaiverCompletedAt,
                        )}
                      </div>
                    )}
                    {waiver.paperWaiverRecordedByName && (
                      <div className="text-gray-500">
                        by {waiver.paperWaiverRecordedByName}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">Needs Waiver</div>
                )}
                <button
                  onClick={() => openFlagModal(waiver)}
                  className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors ${
                    waiver.isFlagged
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {waiver.isFlagged ? (
                    <>
                      <MdFlag size={16} />
                      Unflag
                    </>
                  ) : (
                    <>
                      <MdOutlineFlag size={16} />
                      Flag
                    </>
                  )}
                </button>
              </div>

              {waiver.isFlagged && waiver.flagReason && (
                <div className="mt-4 p-3 bg-red-50 rounded-md border border-red-200">
                  <div className="flex items-start gap-2">
                    <MdFlag
                      className="text-red-600 flex-shrink-0 mt-0.5"
                      size={16}
                    />
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-red-800 uppercase">
                        Flag Reason:
                      </span>
                      <p className="text-sm text-red-900 mt-1">
                        {waiver.flagReason}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />

      {flagModal.isOpen && flagModal.waiver && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closeFlagModal}
            />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div
                    className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                      flagModal.waiver.isFlagged
                        ? 'bg-red-100'
                        : 'bg-yellow-100'
                    } sm:mx-0 sm:h-10 sm:w-10`}
                  >
                    {flagModal.waiver.isFlagged ? (
                      <MdFlag className="text-red-600" size={24} />
                    ) : (
                      <MdOutlineFlag className="text-yellow-600" size={24} />
                    )}
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {flagModal.waiver.isFlagged ? 'Unflag' : 'Flag'} Waiver
                      </h3>
                      <button
                        onClick={closeFlagModal}
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      >
                        <MdClose size={20} />
                      </button>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-600 mb-4">
                        {flagModal.waiver.isFlagged
                          ? `Are you sure you want to unflag the waiver for ${flagModal.waiver.skaterName}?`
                          : `Flag the waiver for ${flagModal.waiver.skaterName}. You can optionally provide a reason.`}
                      </p>
                      <div className="mb-4">
                        <label
                          htmlFor="archivedFlagReason"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Reason (Optional)
                        </label>
                        <textarea
                          id="archivedFlagReason"
                          rows={3}
                          value={flagReason}
                          onChange={e => setFlagReason(e.target.value)}
                          placeholder="Enter reason for flagging..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      {flagModal.waiver.flagReason && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-md">
                          <p className="text-xs font-medium text-gray-700 mb-1">
                            Current Flag Reason:
                          </p>
                          <p className="text-sm text-gray-600">
                            {flagModal.waiver.flagReason}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleFlagWaiver}
                  disabled={isFlaggingWaiver}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    flagModal.waiver.isFlagged
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  } ${isFlaggingWaiver ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isFlaggingWaiver
                    ? 'Processing...'
                    : flagModal.waiver.isFlagged
                      ? 'Unflag Waiver'
                      : 'Flag Waiver'}
                </button>
                <button
                  onClick={closeFlagModal}
                  disabled={isFlaggingWaiver}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchivedWaivers;
